import { useEffect, useState } from "react";
import api from "../utils/api";
import {
  UilEstate,
  UilUtensils,
  UilShoppingCart,
  UilChart,
  UilShoppingBag,
  UilSetting,
  UilFire,
} from "@iconscout/react-unicons";



export const SIDEBAR_CONFIG = [
  {
    id: "dashboard",
    icon: UilEstate,
    heading: "Dashboard",
    link: "/dashboard",
    roles: ["admin", "superAdmin"],
    visible: true,
  },
  {
    id: "table-bookings",
    icon: UilUtensils,
    heading: "Table Bookings",
    link: "/table-booking",
    roles: ["waiter", "admin", "superAdmin"],
    visible: (features) => features?.tableBooking !== false,
  },
  {
    id: "kitchen-display",
    icon: UilFire,
    heading: "Kitchen",
    link: "/kitchen",
    roles: ["kitchen", "admin", "superAdmin"],
    visible: true,
  },
  {
    id: "online-orders",
    icon: UilShoppingCart,
    heading: "Online Orders",
    link: "/orderdetails",
    roles: ["admin", "superAdmin"],
    visible: (features) => features?.onlineOrders !== false,
  },
  {
    id: "takeaway",
    icon: UilShoppingBag,
    heading: "Takeaway",
    link: "/takeaway",
    roles: ["admin", "superAdmin"],
    visible: (features) => features?.takeaway !== false,
  },
  {
    id: "orders-report",
    icon: UilChart,
    heading: "Reports",
    roles: ["admin", "superAdmin"],
    visible: true,
    submenu: [
      { id: "orders-history", heading: "Order Report", link: "/orders-history", roles: ["admin", "superAdmin"], visible: true },
      { id: "customer-report", heading: "Customer Report", link: "/customer-report", roles: ["admin", "superAdmin"], visible: true },
      { id: "product-report", heading: "Product Report", link: "/product-report", roles: ["admin", "superAdmin"], visible: true },
      { id: "other-apps-sale-report", heading: "Summary", link: "/report-other-app-sale", roles: ["admin", "superAdmin"], visible: true },
    ],
  },
  {
    id: "settings-menu",
    icon: UilSetting,
    heading: "Settings",
    link: "/settings",
    roles: ["admin", "superAdmin", "fingliderAdmin"],
    visible: true,
  },
];

// Drill-down destinations shown as sectioned tiles on the dedicated Settings
// page (/settings) — the sidebar's Settings entry is a plain link to that
// page rather than a dropdown, same as Dashboard.
export const SETTINGS_SECTIONS = [
  {
    heading: "Product Management",
    items: [
      { id: "product-products", heading: "Products", emoji: "📦", link: "/product", roles: ["admin", "superAdmin"], visible: true },
      { id: "product-category", heading: "Category", emoji: "🗂️", link: "/categories", roles: ["admin", "superAdmin"], visible: true },
      { id: "product-subcategory", heading: "Subcategory", emoji: "📁", link: "/subcategories", roles: ["admin", "superAdmin"], visible: true },
      { id: "product-combos", heading: "Combos", emoji: "🍱", link: "/combos", roles: ["admin", "superAdmin"], visible: true },
      { id: "product-addons", heading: "Add-ons", emoji: "➕", link: "/addons", roles: ["admin", "superAdmin"], visible: true },
      { id: "product-update", heading: "Product Availability/Price Update", emoji: "💲", link: "/price-update", roles: ["admin", "superAdmin"], visible: true },
      { id: "product-variant-units", heading: "Variant Units", emoji: "📏", link: "/variant-units", roles: ["admin", "superAdmin"], visible: true },
      { id: "product-status", heading: "Product Status", emoji: "📈", link: "/reports", roles: ["superAdmin"], visible: true },
    ],
  },
  {
    heading: "Sales & Marketing",
    items: [
      { id: "banner", heading: "Banner", emoji: "🖼️", link: "/banner", roles: ["admin", "superAdmin"], visible: true },
      { id: "other-apps-sale", heading: "Other Apps Sale", emoji: "🧾", link: "/other-apps-sale", roles: ["admin", "superAdmin"], visible: true },
    ],
  },
  {
    heading: "Users",
    items: [
      { id: "user-management", heading: "User Management", emoji: "👥", link: "/addusers", roles: ["admin", "superAdmin"], visible: true },
      { id: "user-feedback", heading: "User Feedback", emoji: "⭐", link: "/feedback", roles: ["admin", "superAdmin"], visible: (features) => features?.feedback !== false },
    ],
  },
  {
    heading: "Restaurant Configuration",
    items: [
      { id: "settings", heading: "Restaurant Settings", emoji: "⚙️", link: "/setting", roles: ["superAdmin"], visible: true },
      { id: "configure", heading: "Configure", emoji: "🛠️", link: "/configure", roles: ["fingliderAdmin"], visible: true },
    ],
  },
];

const hasAccess = (item, role, features, sidebarPermissions) => {
  // If dynamic permissions exist for this item's id, use those
  const allowedRoles = sidebarPermissions && sidebarPermissions[item.id] !== undefined
    ? sidebarPermissions[item.id]
    : item.roles;

  const roleCheck =
    item.visible !== false &&
    (!allowedRoles || allowedRoles.includes(role));

  const featureCheck =
    typeof item.visible === "function"
      ? item.visible(features)
      : true;

  return roleCheck && featureCheck;
};

// Filters SETTINGS_SECTIONS down to the sections/items the given role can
// see, mirroring getSidebarByRole's rules so the Settings page and the
// sidebar always agree on what's accessible.
export const getSettingsSections = (role, features = {}, sidebarPermissions = null) => {
  return SETTINGS_SECTIONS
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => hasAccess(item, role, features, sidebarPermissions)),
    }))
    .filter((section) => section.items.length > 0);
};

export const getSidebarByRole = (role, features = {}, sidebarPermissions = null) => {
  return SIDEBAR_CONFIG
    .filter((item) => hasAccess(item, role, features, sidebarPermissions))
    .map((item) => ({
      ...item,
      submenu: item.submenu
        ? item.submenu.filter((sub) => hasAccess(sub, role, features, sidebarPermissions))
        : undefined,
    }))
    .filter((item) => !item.submenu || item.submenu.length > 0);
};


export const useDashboardData = () => {
  const [cardsData, setCardsData] = useState([
    {
      title: "Today's Sales",
      icon: "shopping",
      color: "bg-blue-200 border-blue-400",
      link: "/orders-history",
      series: [{ name: "Sales", data: [0] }],
    },
    {
      title: "Total Orders",
      icon: "shopping",
      color: "bg-emerald-200 border-emerald-400",
      link: "/orders-history",
      series: [{ name: "Total", data: [0] }],
    },
    {
      title: "Weekly Orders",
      icon: "calendar",
      color: "bg-violet-200 border-violet-400",
      link: "/orders-history",
      series: [{ name: "Weekly", data: [0] }],
    },
    {
      title: "Monthly Orders",
      icon: "calendar",
      color: "bg-rose-200 border-rose-400",
      link: "/orders-history",
      series: [{ name: "Monthly", data: [0] }],
    },
    {
      title: "Customer Rating",
      icon: "check",
      color: "bg-amber-200 border-amber-400",
      link: "/feedback",
      series: [{ name: "Rating", data: [0] }],
    },
    {
      title: "Total Products",
      icon: "box",
      color: "bg-cyan-200 border-cyan-400",
      link: "/product",
      series: [{ name: "Products", data: [0] }],
    },
  ]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get("/orders/order-stats");

        const {
          totalOrders = 0,
          todayOrders = 0,
          weeklyOrders = 0,
          monthlyOrders = 0,
          todaySales = 0,
          totalProducts = 0,
          customerRating = 0,
        } = response.data;

        setCardsData((prevData) =>
          prevData.map((card, index) => {
            let value = 0;

            switch (index) {
              case 0:
                value = todaySales;
                break;
              case 1:
                value = totalOrders;
                break;
              case 2:
                value = weeklyOrders;
                break;
              case 3:
                value = monthlyOrders;
                break;
              case 4:
                value = customerRating;
                break;
              case 5:
                value = totalProducts;
                break;
              default:
                value = 0;
            }

            return {
              ...card,
              series: [{ name: card.title, data: [value] }],
            };
          })
        );
      } catch (error) {
        console.error("Error fetching order data:", error);
      }
    };

    fetchData();
  }, []);

  return cardsData;
};
